import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {generatePath, useParams} from 'react-router-dom';
import * as yup from 'yup';

import {fetchPagedCustomsList} from 'api/profile/customs';
import {BackTo} from 'components/common/BackTo';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {createCustomsUserFx} from 'models/customs/users/table';
import {CreateCustomsUser, CustomsRow, ProfileType} from 'types/profile';

import {CustomsOption} from '../helpers/Option';

import {Actions, BackButton, CustomTitle, Field, FormFields, SubmitButton, Wrapper} from './styled';

type Fields = keyof CreateCustomsUser['registrationRequest'] | 'customsIdList' | 'password_confirm';

type FormField = {
    name: Fields;
    readonly?: boolean;
    type?: 'text' | 'password';
    options?: SelectOption<string>[];
};

export const CreateCustomsUserPage = () => {
    const {t, i18n} = useTranslation();
    const translateValidates = useCallback(
        (key: string, replace?: Record<string, any>) =>
            t(`pages.register.validations.${key}`, replace),
        [i18n.language],
    );
    const translateForm = useCallback(
        (key: string) => t('pages.customsUsers.create.fields.' + key),
        [i18n.language],
    );

    const params = useParams<{id: string}>();

    const schema = useMemo(
        () =>
            yup.object().shape({
                first_name: yup.string().required(translateValidates('required')),
                middle_name: yup.string().notRequired(),
                last_name: yup.string().required(translateValidates('required')),
                contact_email: yup
                    .string()
                    .email(translateValidates('contact_email'))
                    .nullable()
                    .notRequired(),
                email: yup
                    .string()
                    .email(translateValidates('contact_email'))
                    .required(translateValidates('required')),
                password: yup
                    .string()
                    .min(8, translateValidates('password', {length: 8}))
                    .matches(/^(?=.*[A-Za-z])(?=.*\d).*$/, translateValidates('invalid_password'))
                    .required(translateValidates('required')),
                password_confirm: yup
                    .string()
                    .required(translateValidates('required'))
                    .test('password', translateValidates('confirm_password_mismatch'), function(
                        value,
                    ) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        return value === this.parent.password;
                    }),
                phone: yup
                    .string()
                    .transform((value) => (value === '' ? null : value))
                    .required(translateValidates('required'))
                    .typeError(translateValidates('required'))
                    .matches(/^\+?\d{10,14}$/, translateValidates('phone')),
                type: yup.mixed<ProfileType.EMPLOYEE>().notRequired(),
                personal_tin: yup
                    .string()
                    .nullable()
                    .notRequired(),
                customsIdList: yup
                    .array()
                    .typeError(translateValidates('required'))
                    .of(yup.number().required(translateValidates('required')))
                    .min(1, translateValidates('required')),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors},
        handleSubmit,
        setValue,
        setError,
        reset,
    } = useForm<Record<string, any>>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    const onSubmit = async (
        data: CreateCustomsUser['registrationRequest'] & {customsIdList: number[]},
    ) => {
        try {
            const payload: CreateCustomsUser = {
                registrationRequest: {
                    email: data.email,
                    password: data.password,
                    type: ProfileType.EMPLOYEE,
                    first_name: data.first_name,
                    last_name: data.last_name,
                    ...(data.contact_email && {contact_email: data.contact_email}),
                    ...(data.middle_name && {middle_name: data.middle_name}),
                    ...(data.phone && {phone: data.phone}),
                    ...(data.personal_tin && {personal_tin: data.personal_tin}),
                },
                customsIdList: data.customsIdList,
            };

            await createCustomsUserFx(payload);
            reset();
            if (params.id) history.push(generatePath(ROUTE.CUSTOMS_USERS, {id: params.id}));
            else history.push(ROUTE.CUSTOMS_USERS_LIST);
        } catch (error) {
            console.error(error);
            const responseData = error.fullErrorsList;
            if (!Array.isArray(responseData)) return;

            responseData.forEach(({field, message}) => setError(field, {type: 'manual', message}));
        }
    };

    const formFields: FormField[] = useMemo(
        () => [
            {name: 'first_name'},
            {name: 'middle_name'},
            {name: 'last_name'},
            {name: 'email'},
            {name: 'password', type: 'password'},
            {name: 'password_confirm', type: 'password'},
            {name: 'personal_tin'},
            {name: 'phone'},
            {name: 'customsIdList'},
        ],
        [t],
    );

    const [customs, setCustoms] = useState<CustomsRow[]>([]);
    const getCustoms = async () => {
        try {
            const response = (
                await fetchPagedCustomsList({
                    sortings: [],
                    filters: [],
                    paging: {pageSize: 50, pageNumber: 0},
                })
            ).data;
            setCustoms(response);
            if (!params.id) return;
            setValue('customsIdList', [+params.id]);
        } catch (error) {
            console.error(error);
        }
    };
    useEffect(() => {
        getCustoms();
    }, []);
    const customsOptions = useMemo<SelectOption<number | null>[]>(() => {
        try {
            return customs.map((item) => ({
                value: item.id ?? null,
                label: item.name,
                raw: item,
            }));
        } catch (error) {
            console.error(error);
            return [{value: null, label: ''}];
        }
    }, [customs]);

    const controlRender = (
        controlEvents: ControllerRenderProps<Record<string, any>>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
            ...(field.type && {type: field.type}),
            autoComplete: 'new-password',
        };

        if (field.name === 'customsIdList') {
            const selectedOptions = customsOptions.filter((o) =>
                controlEvents.value.includes(o.value),
            );

            return (
                <div style={{paddingBottom: '6px'}}>
                    <Select
                        menuPlacement="top"
                        isMulti
                        value={selectedOptions}
                        options={customsOptions}
                        {...baseProps}
                        onChange={(selected: SelectOption<number>[]) => {
                            const values = selected.map((o) => o.value);
                            controlEvents.onChange(values);
                        }}
                        optionComponent={CustomsOption}
                    />
                </div>
            );
        }

        const props = {...baseProps, ...controlEvents};
        return <Input {...props} />;
    };

    const backPath = params.id
        ? generatePath(ROUTE.CUSTOMS_USERS, {id: params.id})
        : ROUTE.CUSTOMS_USERS_LIST;

    return (
        <>
            <BackTo to={backPath}>{t('pages.profile.staff.create.back')}</BackTo>
            <ContentContainer>
                <Wrapper>
                    <form
                        autoComplete="off"
                        onSubmit={handleSubmit(onSubmit, console.error)}
                        style={{marginTop: 50}}
                    >
                        <CustomTitle size={4}>{t('pages.profile.staff.create.title')}</CustomTitle>
                        <FormFields>
                            {formFields.map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>
                                        {t(`pages.customsUsers.create.fields.${field.name}`)}
                                    </span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                        </FormFields>
                        <Actions>
                            <BackButton
                                type="button"
                                variant={ButtonVariant.Outline}
                                onClick={() => {
                                    history.push(backPath);
                                    reset();
                                }}
                            >
                                {t('pages.profile.staff.create.cancel')}
                            </BackButton>
                            <SubmitButton type="submit">
                                {t('pages.profile.staff.create.createBtn')}
                            </SubmitButton>
                        </Actions>
                    </form>
                </Wrapper>
            </ContentContainer>
        </>
    );
};
